import axios, { type AxiosResponse } from 'axios';
import type { ApiResponse, User, BrokerAccount, Holding, Alert, SymbolSearchResult, Murmur } from '../types';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.PROD ? '/api' : 'http://localhost:3001/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth tokens
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const apiService = {
  // Health check
  healthCheck: async (): Promise<ApiResponse<{ status: string; timestamp: string }>> => {
    const response = await api.get('/health');
    return response.data;
  },

  // Test database connection
  testDb: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.get('/test-db');
    return response.data;
  },

  // Auth endpoints
  auth: {
    login: async (credentials: { email: string; password: string }): Promise<ApiResponse<{ user: User; access_token: string; refresh_token: string }>> => {
      const response = await api.post('/auth/login', credentials);
      return response.data;
    },

    // NOTE: Email/password registration is disabled - only Google OAuth is allowed
    register: async (userData: { email: string; password: string; name: string; phone?: string }): Promise<ApiResponse<{ user: User; access_token: string; refresh_token: string }>> => {
      const response = await api.post('/auth/register', userData);
      return response.data;
    },

    // Google OAuth login - send JWT credential to backend for verification
    googleLogin: async (googleToken: string): Promise<ApiResponse<{ user: User; token: string }>> => {
      const response = await api.post('/auth/google', { credential: googleToken });
      return response.data;
    },

    refreshToken: async (): Promise<ApiResponse<{ access_token: string; refresh_token: string }>> => {
      const refreshToken = localStorage.getItem('refreshToken');
      const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
      return response.data;
    },

    logout: async (): Promise<ApiResponse<{}>> => {
      const response = await api.post('/auth/logout');
      return response.data;
    },

    logoutAll: async (): Promise<ApiResponse<{}>> => {
      const response = await api.post('/auth/logout-all');
      return response.data;
    },

    getProfile: async (): Promise<ApiResponse<User>> => {
      const response = await api.get('/auth/profile');
      return response.data;
    },

    verifyToken: async (): Promise<ApiResponse<{ user: User }>> => {
      const response = await api.get('/auth/verify');
      return response.data;
    },
  },

  // Broker accounts
  brokers: {
    getAccounts: async (): Promise<ApiResponse<BrokerAccount[]>> => {
      const response = await api.get('/brokers');
      return response.data;
    },
    connect: async (brokerData: { broker_type: string; account_name: string; api_key: string; api_secret?: string }): Promise<ApiResponse<any>> => {
      const response = await api.post('/brokers/connect', brokerData);
      return response.data;
    },
    sync: async (accountId: string, syncData?: any): Promise<ApiResponse<any>> => {
      const response = await api.post(`/brokers/${accountId}/sync`, syncData);
      return response.data;
    },
    delete: async (accountId: string): Promise<ApiResponse<{}>> => {
      const response = await api.delete(`/brokers/${accountId}`);
      return response.data;
    },
  },

  // Holdings
  holdings: {
    getAll: async (): Promise<ApiResponse<Holding[]>> => {
      const response = await api.get('/holdings');
      return response.data;
    },
    getByAccount: async (accountId: string): Promise<ApiResponse<Holding[]>> => {
      const response = await api.get(`/brokers/holdings/account/${accountId}`);
      return response.data;
    },
    // New endpoints for user-added holdings management
    uploadCSV: async (csvData: string): Promise<ApiResponse<{ message: string; count: number; holdings: any[] }>> => {
      const response = await api.post('/holdings/upload-csv', { csvData });
      return response.data;
    },
    addManual: async (holdingData: { symbol: string; exchange?: string; quantity: number; average_price: number }): Promise<ApiResponse<Holding>> => {
      const response = await api.post('/holdings/manual', holdingData);
      return response.data;
    },
    delete: async (holdingId: string): Promise<ApiResponse<{ message: string }>> => {
      const response = await api.delete(`/holdings/${holdingId}`);
      return response.data;
    },
    getUserAdded: async (): Promise<ApiResponse<Holding[]>> => {
      const response = await api.get('/holdings/user-added');
      return response.data;
    },
    deleteAll: async (): Promise<ApiResponse<{ message: string; deletedCount: number }>> => {
      const response = await api.delete('/holdings/all');
      return response.data;
    },
    update: async (holdingId: string, updateData: { quantity?: number; average_price?: number }): Promise<ApiResponse<Holding>> => {
      const response = await api.put(`/holdings/${holdingId}`, updateData);
      return response.data;
    },
  },

  // Alerts
  alerts: {
    getAll: async (): Promise<ApiResponse<Alert[]>> => {
      const response = await api.get('/alerts');
      return response.data;
    },
    // Triggered alerts history (default 30 days)
    getHistory: async (days: number = 30): Promise<ApiResponse<Alert[]>> => {
      const response = await api.get(`/alerts/history?days=${days}`);
      return response.data;
    },
    create: async (alertData: Omit<Alert, 'id' | 'user_id' | 'created_at' | 'triggered_at'>): Promise<ApiResponse<Alert>> => {
      const response = await api.post('/alerts', alertData);
      return response.data;
    },
    update: async (alertId: string, alertData: Partial<Alert>): Promise<ApiResponse<Alert>> => {
      const response = await api.put(`/alerts/${alertId}`, alertData);
      return response.data;
    },
    delete: async (alertId: string): Promise<ApiResponse<{}>> => {
      const response = await api.delete(`/alerts/${alertId}`);
      return response.data;
    },
    getQuota: async (): Promise<ApiResponse<{ used: number; total: number; subscription_tier: string }>> => {
      const response = await api.get('/alerts/quota');
      return response.data;
    },
  },

  // Symbols
  symbols: {
    search: async (params: { query: string; exchange?: string; limit?: number; include_inactive?: boolean }): Promise<ApiResponse<SymbolSearchResult[]>> => {
      const response = await api.get('/symbols/search', { params });
      return response.data;
    },
    validate: async (data: { symbol: string; exchange: string }): Promise<ApiResponse<any>> => {
      const response = await api.post('/symbols/validate', data);
      return response.data;
    },
    getDetails: async (symbol: string, exchange: string): Promise<ApiResponse<any>> => {
      const response = await api.get(`/symbols/details/${symbol}/${exchange}`);
      return response.data;
    },
    getStats: async (): Promise<ApiResponse<any>> => {
      const response = await api.get('/symbols/stats');
      return response.data;
    },
  },

  // Position Rules
  positionRules: {
    create: async (rule: Partial<any>): Promise<ApiResponse<any>> => {
      const response = await api.post('/position-rules', rule);
      return response.data;
    },
    bulkCreate: async (rules: Partial<any>[]): Promise<ApiResponse<any[]>> => {
      const response = await api.post('/position-rules/bulk', rules);
      return response.data;
    },
    delete: async (ruleId: string): Promise<ApiResponse<{}>> => {
      const response = await api.delete(`/position-rules/${ruleId}`);
      return response.data;
    },
    list: async (): Promise<ApiResponse<any[]>> => {
      const response = await api.get('/position-rules');
      return response.data;
    },
    getCurrentPrices: async (symbols: string[]): Promise<ApiResponse<Record<string, number>>> => {
      const response = await api.get('/position-rules/current-prices', {
        params: { symbols }
      });
      return response.data;
    },
  },

  getRuleExecutions: async (params: { start_date?: string; end_date?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params.start_date) query.append('start_date', params.start_date);
    if (params.end_date) query.append('end_date', params.end_date);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());
    const response = await api.get(`/rule-executions?${query.toString()}`);
    return response.data;
  },

  // Growth CRM
  growth: {
    getCohorts: async (): Promise<ApiResponse<string>> => {
      const response = await api.get('/growth/cohorts');
      return response.data;
    },
    getTemplates: async (): Promise<ApiResponse<string[]>> => {
      const response = await api.get('/growth/templates');
      return response.data;
    },
    getTemplate: async (id: string): Promise<ApiResponse<string>> => {
      const response = await api.get(`/growth/templates/${id}`);
      return response.data;
    },
    saveTemplate: async (id: string, content: string): Promise<ApiResponse<any>> => {
      const response = await api.post(`/growth/templates/${id}`, { content });
      return response.data;
    },
    sendCampaign: async (cohort: string, dryRun: boolean, recipientsCsv?: string, sender?: string, senderName?: string): Promise<ApiResponse<string>> => {
      const response = await api.post('/growth/campaigns/send',
        { cohort, dryRun, recipientsCsv, sender, senderName },
        { timeout: 300000 } // 5 minute timeout for large campaigns
      );
      return response.data;
    },
    getRecipients: async (cohortId: string): Promise<ApiResponse<string>> => {
      const response = await api.get(`/growth/cohorts/${cohortId}/recipients`);
      return response.data;
    },
  },

  // Murmurs
  murmurs: {
    get: async (params?: { start_date?: string; end_date?: string; symbol?: string; limit?: number; offset?: number; sort_by?: string; sort_order?: string; min_price?: number | string; max_price?: number | string; min_value?: number | string }): Promise<ApiResponse<Murmur[]>> => {
      const response = await api.get('/murmurs', { params });
      return response.data;
    },
  },
};

export const notificationEndpointsApi = {
  async getEndpoints() {
    const res = await api.get('/notification-endpoints');
    return res.data;
  },
  async addEndpoint(data: { type: string; value: string }) {
    const res = await api.post('/notification-endpoints', data);
    return res.data;
  },
  async resendVerification(id: string) {
    const res = await api.post(`/notification-endpoints/${id}/resend`);
    return res.data;
  },
  async verifyEndpoint(token: string) {
    const res = await api.get(`/notification-endpoints/verify?token=${encodeURIComponent(token)}`);
    return res.data;
  },
  async deleteEndpoint(id: string) {
    const res = await api.delete(`/notification-endpoints/${id}`);
    return res.data;
  },
};

export default api; 